import type { Dispatch, SetStateAction } from "react";
import { AlertTriangle, Loader2, Send, X } from "lucide-react";
import { rotuloDoPrazo, type PrazoDoLaboratorio } from "@bookdental/schemas";
import { FilesDropzone } from "@/components/files/FilesDropzone";
import { FileAttachment } from "@/components/shared/FileAttachment";
import { formatBRL } from "./prosthesis-meta";
import { ServiceSummaryPopover } from "./ServiceSummaryPopover";
import { AttachmentChannelPicker } from "./AttachmentChannelPicker";
import type { ServiceDraft } from "./prosthesis-service-draft";
import { cn } from "@/lib/cn";
import type { PrazoModo } from "./ProsthesisRequestForm";
import { totalParaExibir } from "@/components/prosthesis/valor-do-pedido";

const inputCls =
  "h-9 w-full rounded-md border border-zinc-200 bg-white px-3 text-sm text-zinc-900 placeholder:text-zinc-400 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-border dark:bg-card dark:text-foreground";
const labelCls = "mb-1 block text-xs font-medium text-zinc-600 dark:text-muted-foreground";
const presetCls =
  "rounded-md border border-zinc-200 px-2.5 py-1 text-[12px] font-medium text-zinc-700 transition-colors hover:bg-zinc-50 dark:border-border dark:text-foreground dark:hover:bg-muted";
const presetAtivoCls =
  "border-brand-500 bg-brand-50 text-brand-700 hover:bg-brand-50 dark:border-primary dark:bg-primary/10 dark:text-primary";
const SEIS = 6;

type Props = {
  services: ServiceDraft[];
  onRemove: (index: number) => void;
  isPending: boolean;
  /** "editar" troca o botão de "Enviar pedido" por "Salvar alterações" — o pedido já foi enviado. */
  modo?: "criar" | "editar";
  deadline: string;
  onDeadlineChange: (value: string) => void;
  deadlineMode: PrazoModo;
  onDeadlinePreset: (modo: PrazoModo) => void;
  description: string;
  onDescriptionChange: (value: string) => void;
  prazo: PrazoDoLaboratorio | null;
  isUrgent: boolean;
  attachments: { fileId: string; fileName: string }[];
  onAttachmentsChange: Dispatch<SetStateAction<{ fileId: string; fileName: string }[]>>;
  attachmentChannels: string[];
  onAttachmentChannelsChange: (channels: string[]) => void;
};

/** Painel "Detalhes do pedido" — lista os serviços adicionados + prazo/observações do pedido +
 *  total + botão de enviar. */
export function ProsthesisOrderSummaryPanel({
  services,
  onRemove,
  isPending,
  modo = "criar",
  deadline,
  onDeadlineChange,
  deadlineMode,
  onDeadlinePreset,
  description,
  onDescriptionChange,
  prazo,
  isUrgent,
  attachments,
  onAttachmentsChange,
  attachmentChannels,
  onAttachmentChannelsChange,
}: Props) {
  // Prazo do laboratório de 6 dias corridos é o mesmo que "6 dias": vira um botão só.
  const labIgualSeis = prazo != null && prazo.dias === SEIS && !prazo.diasUteis;
  const seisAtivo = deadlineMode === "seis" || (deadlineMode === "lab" && (!prazo || labIgualSeis));
  const totalCents = totalParaExibir(services);

  return (
    <div className="flex w-full shrink-0 flex-col rounded-xl border border-zinc-200 bg-white p-5 shadow-sm dark:border-border dark:bg-card lg:w-[300px]">
      <h3 className="mb-3 text-sm font-semibold text-zinc-900 dark:text-foreground">
        Detalhes do Pedido{services.length > 0 ? ` (${services.length})` : ""}
      </h3>
      <div className="flex-1 space-y-4">
        {services.length === 0 ? (
          <p className="text-xs text-zinc-500 dark:text-muted-foreground">
            Nenhum serviço adicionado ainda. Preencha os campos ao lado e clique em "Adicionar
            serviço".
          </p>
        ) : (
          <>
            <ul className="space-y-2">
              {services.map((s, i) => (
                <li
                  key={i}
                  className="flex items-start justify-between gap-3 rounded-lg border border-zinc-100 bg-zinc-50/60 px-3 py-2 dark:border-border dark:bg-muted/30"
                >
                  <div className="min-w-0 space-y-0.5">
                    <p className="text-sm font-medium text-zinc-900 dark:text-foreground">{s.serviceCategoryLabel}</p>
                    <p className="text-[11px] text-zinc-500 dark:text-muted-foreground">
                      Dentes/região: {s.teeth || "—"}
                    </p>
                    {s.materialOptionLabel && (
                      <p className="text-[11px] text-zinc-500 dark:text-muted-foreground">
                        Material: {s.materialOptionLabel}
                      </p>
                    )}
                    {s.shadeOptionLabel && (
                      <p className="text-[11px] text-zinc-500 dark:text-muted-foreground">
                        Cor: {s.shadeOptionLabel}
                      </p>
                    )}
                  </div>
                  <div className="flex shrink-0 items-center gap-3">
                    <ServiceSummaryPopover service={s} />
                    <button
                      type="button"
                      onClick={() => onRemove(i)}
                      className="text-zinc-400 transition-colors hover:text-red-600"
                      aria-label="Remover serviço"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
            <div className="flex justify-end gap-1.5 text-sm">
              <span className="text-zinc-500 dark:text-muted-foreground">Total:</span>
              <span className="font-semibold tabular-nums text-zinc-900 dark:text-foreground">
                {formatBRL(totalCents)}
              </span>
            </div>
          </>
        )}

        <div className="border-t border-zinc-100 pt-4 dark:border-border">
          <label className={labelCls}>Prazo desejado do pedido</label>
          {/* Os dois hábitos de quem pede: o prazo do laboratório e "seis dias" (o paciente volta em
              sete). Sem laboratório escolhido não há "prazo do laboratório" pra oferecer, e quando os
              dois dão o mesmo número vira um botão só — dois botões iguais confundem. */}
          <div className="mb-2 flex flex-wrap gap-1.5">
            {prazo != null && !labIgualSeis && (
              <button
                type="button"
                onClick={() => onDeadlinePreset("lab")}
                aria-pressed={deadlineMode === "lab"}
                className={cn(presetCls, deadlineMode === "lab" && presetAtivoCls)}
              >
                Prazo do laboratório ({rotuloDoPrazo(prazo)})
              </button>
            )}
            <button
              type="button"
              onClick={() => onDeadlinePreset("seis")}
              aria-pressed={seisAtivo}
              className={cn(presetCls, seisAtivo && presetAtivoCls)}
            >
              {labIgualSeis ? "Prazo do laboratório (6 dias)" : "6 dias"}
            </button>
          </div>
          <input
            type="date"
            value={deadline}
            onChange={(e) => onDeadlineChange(e.target.value)}
            className={inputCls}
          />
          {prazo != null && (
            <p className="mt-1 text-[11px] text-zinc-500 dark:text-muted-foreground">
              Prazo previsto do laboratório: {rotuloDoPrazo(prazo)}.
            </p>
          )}
          {isUrgent && (
            <div className="mt-2 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-2.5 text-[11.5px] leading-relaxed text-amber-800 dark:border-amber-900/40 dark:bg-amber-950/20 dark:text-amber-300">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <p>
                Esse prazo é mais curto que o previsto pelo laboratório — o pedido será avaliado como{" "}
                <strong>urgente</strong> e pode ser cobrada uma taxa extra pelo laboratório.
              </p>
            </div>
          )}
        </div>

        <div>
          <label className={labelCls}>Observações do Pedido</label>
          <textarea
            value={description}
            onChange={(e) => onDescriptionChange(e.target.value)}
            rows={3}
            placeholder="Detalhes do pedido, instruções ao laboratório..."
            // `resize-none` como no resto do app: sem isso o navegador deixa arrastar o canto, e no
            // celular um toque arrastado durante a rolagem encolhia o campo sem querer — a altura
            // ficava, porque a partir daí o `rows` não vale mais.
            className="w-full resize-none rounded-md border border-zinc-200 bg-white px-3 py-2 text-sm text-zinc-900 placeholder:text-zinc-400 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-border dark:bg-card dark:text-foreground"
          />
        </div>

        <div>
          <label className={labelCls}>Anexos do Pedido (opcional)</label>
          {attachments.length > 0 && (
            <div className="mb-2 flex flex-col gap-1.5 sm:flex-row sm:flex-wrap">
              {attachments.map((a, i) => (
                <FileAttachment
                  key={a.fileId}
                  fileId={a.fileId}
                  fileName={a.fileName}
                  onRemove={() => onAttachmentsChange((atuais) => atuais.filter((_, j) => j !== i))}
                />
              ))}
            </div>
          )}
          <FilesDropzone
            kind="PROSTHESIS_ATTACHMENT"
            // Vários arquivos sobem juntos: cada um soma à lista atual, não à de quando o envio começou.
            onUploaded={(file) => onAttachmentsChange((atuais) => [...atuais, { fileId: file.fileId, fileName: file.name }])}
          />
        </div>

        <div>
          <label className={labelCls}>Enviado também por</label>
          <p className="mb-1.5 text-[11px] text-zinc-500 dark:text-muted-foreground">
            Nem sempre o pedido vai só pela plataforma — marque se combinou também por outro canal.
          </p>
          <AttachmentChannelPicker value={attachmentChannels} onChange={onAttachmentChannelsChange} />
        </div>
      </div>

      <button
        type="submit"
        form="prosthesis-order-form"
        disabled={isPending}
        className="mt-4 inline-flex h-10 w-full shrink-0 items-center justify-center gap-2 rounded-md bg-brand-600 px-5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-brand-700 disabled:opacity-60"
      >
        {isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        {modo === "editar" ? "Salvar alterações" : "Enviar pedido"}
      </button>
    </div>
  );
}
